import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { FeatureConditions } from '@app/model/feature-condition.model';
import {
    ElectricityCostsAndSavings,
    EnergyAmountsAndCostsOverDay,
    EnergyTabData,
    SolarInstallationInformation,
    SolarStorageInformation,
} from '@app/models/client-informations.model';
import { Client, CloudScheduleParameters } from '@app/models/client.model';
import { Country } from '@app/models/enum/country.enum';
import { byPassError } from '@app/models/http-context.model';
import { LocationInput } from '@app/models/location.model';
import { ClientSignedContract, getDateAtMidnight, getTimezoneOffsetForIot } from '@web/utils-common';
import { Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class ClientService {
    public readonly tag = '[ClientService]';
    private readonly http = inject(HttpClient);
    public country?: string;
    public hasGridStreamMeter = signal(false);
    public hasErl = signal(false);
    public hasStorageBattery = signal(false);
    public hasConsumptionDevice = computed(() => {
        return this.hasGridStreamMeter() || this.hasErl();
    });
    public shouldPromptRating = false;

    /** Has the user already acknowledged the warning shown before forcing a consumption device on. */
    public forcedOnAcknowledged = signal(false);

    private readonly rootUrl = `${environment.urlBackend}/api/client`;

    public updateCurrentClientCountry(client: Client | undefined) {
        this.country = client?.country ?? Country.FR;
    }

    public updateCurrentCountry(country: Country) {
        this.country = country;
    }

    public get(): Observable<Client> {
        return this.http.get<Client>(this.rootUrl).pipe(
            tap(client => {
                this.updateCurrentClientCountry(client);
                this.hasGridStreamMeter.set(client.hasGridStreamMeter ?? false);
                this.hasErl.set(client.hasErl ?? false);
                this.hasStorageBattery.set(client.hasStorageBattery ?? false);
                this.shouldPromptRating = client.ratingPrompt;
                this.forcedOnAcknowledged.set(client.forcedOnAcknowledged ?? false);
            }),
        );
    }

    public update(newClient: Client): Observable<Client> {
        return this.http.put<Client>(this.rootUrl, newClient).pipe(
            tap(client => {
                this.updateCurrentClientCountry(client);
                this.hasGridStreamMeter.set(client.hasGridStreamMeter ?? false);
                this.hasErl.set(client.hasErl ?? false);
                this.hasStorageBattery.set(client.hasStorageBattery ?? false);
            }),
        );
    }

    public patchCloudScheduleParameter(
        cloudScheduleParameter: CloudScheduleParameters | null,
        existingClient?: Client,
    ): Observable<Client> {
        if (existingClient) {
            return this.update({ ...existingClient, cloudScheduleParameter });
        }
        return this.get().pipe(switchMap(client => this.update({ ...client, cloudScheduleParameter })));
    }

    public updateReview(ratingDone: boolean, ratingPrompted: boolean): Observable<Client> {
        this.shouldPromptRating = false;
        return this.http.put<Client>(`${this.rootUrl}/rating`, { ratingDone, ratingPrompted }).pipe(
            tap(client => {
                console.log('Client rating updated', ratingDone, ratingPrompted, client.ratingPrompt);
            }),
        );
    }

    public updateDynamicChargeThreshold(dynamicChargeThreshold: number): Observable<Client> {
        return this.http
            .put<Client>(`${this.rootUrl}/update-dynamic-charge-threshold`, { dynamicChargeThreshold })
            .pipe(
                tap(client => {
                    console.log(
                        'Client dynamic charge threshold updated',
                        dynamicChargeThreshold,
                        client.dynamicThreshold,
                    );
                }),
            );
    }

    public validateLocation(location: LocationInput): Observable<Client> {
        return this.http
            .put<Client>(`${this.rootUrl}/validate-location`, location, {
                context: byPassError(),
            })
            .pipe(tap(client => this.updateCurrentClientCountry(client)));
    }

    public validateOnboarding(): Observable<Client> {
        return this.http.put<Client>(`${this.rootUrl}/validate-onboarding`, null);
    }

    /** Records that the user ticked "don't show this again" in the forced activation warning modal. */
    public acknowledgeForcedOn(): Observable<Client> {
        return this.http
            .put<Client>(`${this.rootUrl}/acknowledge-forced-on`, null)
            .pipe(tap(client => this.forcedOnAcknowledged.set(client.forcedOnAcknowledged ?? false)));
    }

    public getContract(): Observable<ClientSignedContract> {
        return this.http.get<ClientSignedContract>(`${this.rootUrl}/clientSignedContract`, {
            context: byPassError(),
        });
    }

    public saveContract(clientSignedContract: ClientSignedContract): Observable<Client> {
        return this.http.post<Client>(`${this.rootUrl}/clientSignedContract`, clientSignedContract);
    }

    public isFeatureAllowed(conditions: FeatureConditions): boolean {
        const erlCondition =
            conditions.hasErl == undefined || this.country == Country.FR ? true : conditions.hasErl == this.hasErl();
        return this.hasAnyCountry(conditions.enabledCountries) && erlCondition;
    }

    public getSolarInstallationInformation(): Observable<SolarInstallationInformation> {
        return this.http.get<SolarInstallationInformation>(`${this.rootUrl}/solar-installation-information`);
    }

    public updateSolarInstallationInformation(
        solarInstallationInformation: SolarInstallationInformation,
    ): Observable<SolarInstallationInformation> {
        return this.http.put<SolarInstallationInformation>(
            `${this.rootUrl}/solar-installation-information`,
            solarInstallationInformation,
        );
    }

    public getSolarStorageInformation(): Observable<SolarStorageInformation> {
        return this.http.get<SolarStorageInformation>(`${this.rootUrl}/solar-storage-information`);
    }

    public updateSolarStorageInformation(
        solarStorageInformation: SolarStorageInformation,
    ): Observable<SolarStorageInformation> {
        return this.http.put<SolarStorageInformation>(
            `${this.rootUrl}/solar-storage-information`,
            solarStorageInformation,
        );
    }

    public getEnergyTabDisplayData(): Observable<EnergyTabData> {
        return this.http.get<EnergyTabData>(`${this.rootUrl}/shouldShowEnergyTab`);
    }

    public getElectricityCostsAndSavings(): Observable<ElectricityCostsAndSavings> {
        const timeZone = getTimezoneOffsetForIot();
        return this.http.get<ElectricityCostsAndSavings>(`${this.rootUrl}/electricityCosts`, {
            params: { zone: timeZone },
        });
    }

    public getEnergyAmountsAndCostsForDay(day?: Date): Observable<EnergyAmountsAndCostsOverDay> {
        const date: Date = getDateAtMidnight(day ?? new Date());
        const timeZone = getTimezoneOffsetForIot();
        return this.http.get<EnergyAmountsAndCostsOverDay>(`${this.rootUrl}/energyAmountsAndCostsForDay`, {
            params: { zone: timeZone, day: date.toISOString() },
        });
    }

    private hasAnyCountry(countries: string[]): boolean {
        const clientCountry = Country[this.country as keyof typeof Country];
        if (!countries) {
            return false;
        }
        if (!Array.isArray(countries)) {
            countries = [countries];
        }
        return countries.includes(clientCountry);
    }
}
